// VRP Notification Context - Unified Notification System
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { VRPNotification, NotificationAction } from '../types/ui';

interface VRPNotificationContextType {
  notifications: VRPNotification[];
  showNotification: (notification: Omit<VRPNotification, 'id' | 'timestamp'>) => string;
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
  getNotificationById: (id: string) => VRPNotification | undefined;
}

const VRPNotificationContext = createContext<VRPNotificationContextType | null>(null);

interface VRPNotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
  defaultDuration?: number;
}

export const VRPNotificationProvider: React.FC<VRPNotificationProviderProps> = ({
  children,
  maxNotifications = 10,
  defaultDuration = 5000
}) => {
  const [notifications, setNotifications] = useState<VRPNotification[]>([]);

  const showNotification = useCallback((
    notification: Omit<VRPNotification, 'id' | 'timestamp'>
  ): string => {
    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();
    
    const fullNotification: VRPNotification = {
      ...notification,
      id,
      timestamp,
      duration: notification.duration ?? defaultDuration
    };

    setNotifications(prev => {
      const updated = [...prev, fullNotification];
      
      // Remove oldest notifications if we exceed the limit
      if (updated.length > maxNotifications) {
        return updated.slice(-maxNotifications);
      }
      
      return updated;
    });

    // Auto-dismiss if duration is set and not persistent
    if (fullNotification.duration && !fullNotification.persistent) {
      setTimeout(() => {
        dismissNotification(id);
      }, fullNotification.duration);
    }

    return id;
  }, [maxNotifications, defaultDuration]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getNotificationById = useCallback((id: string) => {
    return notifications.find(notification => notification.id === id);
  }, [notifications]);

  const value: VRPNotificationContextType = {
    notifications,
    showNotification,
    dismissNotification,
    clearAllNotifications,
    getNotificationById
  };

  return (
    <VRPNotificationContext.Provider value={value}>
      {children}
    </VRPNotificationContext.Provider>
  );
};

export const useVRPNotifications = (): VRPNotificationContextType => {
  const context = useContext(VRPNotificationContext);
  if (!context) {
    throw new Error('useVRPNotifications must be used within a VRPNotificationProvider');
  }
  return context;
};

// Convenience hooks for different notification types
export const useNotify = () => {
  const { showNotification } = useVRPNotifications();

  return {
    success: (message: string, title?: string, actions?: NotificationAction[]) =>
      showNotification({
        type: 'success',
        title: title || 'Success',
        message,
        actions
      }),

    error: (message: string, title?: string, persistent = true) =>
      showNotification({
        type: 'error',
        title: title || 'Error',
        message,
        persistent
      }),

    warning: (message: string, title?: string) =>
      showNotification({
        type: 'warning',
        title: title || 'Warning',
        message
      }),

    info: (message: string, title?: string) =>
      showNotification({
        type: 'info',
        title: title || 'Information',
        message
      })
  };
};